import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'

interface Props {
  onChange: (value: boolean) => void
  disabled?: boolean
}

const DIMMED_OPACITY = 0.35

interface YesNoToggleProps {
  value: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
  yesLabel: string
  noLabel: string
}

const YesNoToggle = ({ value, onChange, disabled = false, yesLabel, noLabel }: YesNoToggleProps) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    disabled={disabled}
    value={value ? 'yes' : 'no'}
    onChange={(_, newValue: string | null) => {
      if (newValue !== null) onChange(newValue === 'yes')
    }}
    color="primary"
    sx={{ '& .MuiToggleButton-root': { px: 3, textTransform: 'none' } }}
  >
    <ToggleButton value="yes">{yesLabel}</ToggleButton>
    <ToggleButton value="no">{noLabel}</ToggleButton>
  </ToggleButtonGroup>
)

// Two-question filter flow: the derived flag sent to the form is (question 1 && question 2).
// Answers are kept locally because only the derived isSymTechPrime is persisted.
const SymTechPrimeQuestion = ({ onChange, disabled = false }: Props) => {
  const { t } = useTranslation()
  const [isSymTechProduct, setIsSymTechProduct] = useState(false)
  const [isPreferredRateProgram, setIsPreferredRateProgram] = useState(false)

  const handleSymTechProductChanged = (value: boolean) => {
    setIsSymTechProduct(value)
    const preferredRate = value ? isPreferredRateProgram : false
    if (!value) setIsPreferredRateProgram(false)
    onChange(value && preferredRate)
  }

  const handlePreferredRateChanged = (value: boolean) => {
    setIsPreferredRateProgram(value)
    onChange(isSymTechProduct && value)
  }

  return (
    <Box
      component="fieldset"
      sx={{
        border: 1,
        borderColor: 'primary.main',
        borderRadius: 2,
        px: 2,
        pb: 2,
        mx: 0,
        my: 2,
      }}
    >
      <Typography component="legend" variant="subtitle2" fontWeight="bold" color="primary" sx={{ px: 1 }}>
        {t('editCreditApplication.symTechPrime.title')}
      </Typography>

      <Stack spacing={2}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          <Typography variant="body1">
            <Trans i18nKey="editCreditApplication.symTechPrime.symTechProductQuestion" />
          </Typography>
          <YesNoToggle
            value={isSymTechProduct}
            onChange={handleSymTechProductChanged}
            disabled={disabled}
            yesLabel={t('common.yes')}
            noLabel={t('common.no')}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{
            opacity: isSymTechProduct ? 1 : DIMMED_OPACITY,
            transition: 'opacity 0.2s',
            borderLeft: 3,
            borderColor: 'primary.main',
            pl: 2,
          }}
        >
          <Box>
            <Typography variant="body1">
              <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateQuestion" />
            </Typography>
            <Typography variant="body2">
              {t('editCreditApplication.symTechPrime.preferredRateAvailability')}
            </Typography>
            <Typography variant="body2" component="ul" sx={{ my: 0, pl: 3 }}>
              <li>
                <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateManufacturers" />
              </li>
              <li>
                <Trans i18nKey="editCreditApplication.symTechPrime.preferredRateInterest" />
              </li>
            </Typography>
          </Box>
          <YesNoToggle
            value={isPreferredRateProgram}
            onChange={handlePreferredRateChanged}
            disabled={disabled || !isSymTechProduct}
            yesLabel={t('common.yes')}
            noLabel={t('common.no')}
          />
        </Stack>

        <Typography
          variant="body2"
          sx={{
            opacity: isSymTechProduct && isPreferredRateProgram ? 1 : DIMMED_OPACITY,
            transition: 'opacity 0.2s',
          }}
        >
          &bull; {t('editCreditApplication.symTechPrime.note')}
        </Typography>
      </Stack>
    </Box>
  )
}

export default SymTechPrimeQuestion
